/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, TextField, ErrorText } from '@components/atoms'
import InputMask from 'react-input-mask'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	PaymentCardType,
	PaymentStateType,
} from '@redux/index'
import { produce } from 'immer'
import dayjs from 'dayjs'

export const PaymentCard = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const card: PaymentCardType = useAppSelector(state => state.payment.paymentCard)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	// state
	const [isAmex, setIsAmex] = React.useState<boolean>(false)
	const [tempExpirationDate, setTempExpirationDate] = React.useState<string>('')

	React.useEffect(() => {
		if (card.expiredMonth !== '' && card.expiredYear !== '') {
			setTempExpirationDate(card.expiredMonth + card.expiredYear)
		}
	}, [card])

	const onCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value.replace(/\D/g, '')

		const newCard = produce(card, draft => {
			draft.cardNumber = str
		})

		if (str.startsWith('34') || str.startsWith('37')) {
			setIsAmex(true)
		} else {
			setIsAmex(false)
		}

		reduxDispatch(paymentActions.PAYMENT_CARD({ ...newCard }))
	}

	const onExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value.replace(/\D/g, '')
		const newExpiration = str.startsWith('0') || str.startsWith('1') ? str : '0' + str
		const months = [
			'01',
			'02',
			'03',
			'04',
			'05',
			'06',
			'07',
			'08',
			'09',
			'10',
			'11',
			'12',
		]
		const thisYear = dayjs().format('YYYY')
		let tempStr = ''

		if (str.length === 0) {
			tempStr = str
		} else if (str.length === 1) {
			tempStr = str
		} else if (str.length === 2) {
			if (!months.includes(str)) {
				tempStr = months[0]
			} else {
				tempStr = str
			}
		} else if (str.length === 3) {
			tempStr = str
		} else {
			tempStr = str

			const tempMonth = newExpiration.substring(0, 2)
			const month = months.includes(tempMonth) ? tempMonth : '01'
			const tempYear = newExpiration.slice(-2)
			const year =
				Number('20' + tempYear) < Number(thisYear) ? thisYear.slice(-2) : tempYear

			tempStr = month + year

			const newCard = produce(card, draft => {
				draft.expiredMonth = month
				draft.expiredYear = year
			})

			reduxDispatch(paymentActions.PAYMENT_CARD({ ...newCard }))
		}

		setTempExpirationDate(tempStr)
	}

	const onCvcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCard = produce(card, draft => {
			draft.cvc = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CARD({
				...newCard,
			}),
		)
	}

	const onCardNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCard = produce(card, draft => {
			draft.cardName = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CARD({
				...newCard,
			}),
		)
	}

	return (
		<View css={style} className="infoView">
			<View className="infoTitleView">
				<Text category="h3">Card Info</Text>
			</View>

			<View className="infoInnerView">
				<View className="inputView">
					<InputMask
						mask={isAmex ? '9999 999999 99999' : '9999 9999 9999 9999'}
						disabled={false}
						maskChar=""
						onChange={onCardNumberChange}
						value={card.cardNumber}
					>
						{() => (
							<TextField
								label="Card Number *"
								error={errorMessages.card.cardNumber !== ''}
							/>
						)}
					</InputMask>
					{errorMessages.card.cardNumber !== '' && (
						<ErrorText className="errorMsg">{errorMessages.card.cardNumber}</ErrorText>
					)}
				</View>

				<View className="inputOuterView">
					<View className="inputView left">
						<InputMask
							mask="99 / 99"
							disabled={false}
							maskChar=""
							value={tempExpirationDate}
							onChange={onExpirationChange}
						>
							{() => (
								<TextField
									label="Expiration Date (MM / YY) *"
									error={
										errorMessages.card.expiredMonth !== '' ||
										errorMessages.card.expiredYear !== ''
									}
								/>
							)}
						</InputMask>
						{errorMessages.card.expiredMonth !== '' ? (
							<ErrorText className="errorMsg">{errorMessages.card.cardNumber}</ErrorText>
						) : errorMessages.card.expiredYear !== '' ? (
							<ErrorText className="errorMsg">{errorMessages.card.expiredYear}</ErrorText>
						) : null}
					</View>

					<View className="inputView right">
						<InputMask
							mask={isAmex ? '9999' : '999'}
							disabled={false}
							maskChar=""
							value={card.cvc}
							onChange={onCvcChange}
						>
							{() => (
								<TextField label="CVC / CVV *" error={errorMessages.card.cvc !== ''} />
							)}
						</InputMask>
						{errorMessages.card.cvc !== '' && (
							<ErrorText className="errorMsg">{errorMessages.card.cvc}</ErrorText>
						)}
					</View>
				</View>

				<View className="inputView">
					<TextField
						type="text"
						label="Card Name *"
						onChange={onCardNameChange}
						value={card.cardName}
						error={errorMessages.card.cardName !== ''}
					/>
					{errorMessages.card.cvc !== '' && (
						<ErrorText className="errorMsg">{errorMessages.card.cardName}</ErrorText>
					)}
				</View>
			</View>
		</View>
	)
}

const style = css({})
